import { Box, Stack, Typography } from '@mui/material'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import { FC } from 'react'
import { ListContextProvider, useList, useTranslate } from 'react-admin'
import { Link as RouterLink } from 'react-router-dom'
import { EmptyState } from '../../components/ui'
import { border } from '../../layout/theme/border'
import { themeColor } from '../../layout/theme/color'
import { monoFontFamily } from '../../layout/theme/semanticTokens'
import { AuditLogOut, CertificateOut, MunicipalityOut } from '../../types/apiRequests'
import { dateFormatter } from '../../utils/dateFormatter'
import { getCommonName } from '../../utils/getCommonName'
import { CertificateDatagrid } from './CertificateColumns'

const muted = { fontSize: 12.5, color: themeColor.slate50 } as const

/** Page size of the related lists (the API maximum). */
export const RELATED_LIMIT = 100

/** "Laatste 100 van 240" when a related list is cut off, else undefined. */
export const useLimitHint = (total: number | undefined): string | undefined => {
  const translate = useTranslate()
  return total && total > RELATED_LIMIT ? translate('ui.limited', { limit: RELATED_LIMIT, total }) : undefined
}

interface CertificatesTableProps {
  certificates: CertificateOut[] | undefined
  isLoading: boolean
  /** Opens the certificate's show page; false when the row's certificate is not reachable (other organization). */
  clickable: boolean
  emptyTitle: string
  emptyText: string
}

/** Certificates of a municipality/organization, same columns as the main list but from a fixed array. */
export const CertificatesTable: FC<CertificatesTableProps> = ({ certificates, isLoading, clickable, emptyTitle, emptyText }) => {
  // Client-side list context so Datagrid can render rows fetched elsewhere.
  const listContext = useList<CertificateOut>({ data: certificates, isLoading, perPage: 500 })

  if (!isLoading && (!certificates || certificates.length === 0)) {
    return (
      <EmptyState
        compact
        icon={<WorkspacePremiumOutlinedIcon />}
        title={emptyTitle}
        text={emptyText}
      />
    )
  }

  return (
    <ListContextProvider value={listContext}>
      <CertificateDatagrid rowClick={clickable ? (id) => `/certificate/${id}/show` : false} />
    </ListContextProvider>
  )
}

interface AuditTimelineProps {
  logs: AuditLogOut[] | undefined
  isLoading: boolean
  error?: unknown
  /** Per row: the certificate's hostname and municipality. */
  certificates?: CertificateOut[]
  municipalities?: MunicipalityOut[]
  /** Hostname links to the certificate; false when its show page is not reachable (other organization). */
  linkCertificates?: boolean
}

/** Audit log timeline, per row "hostname · gemeente" when known. */
export const AuditTimeline: FC<AuditTimelineProps> = ({
  logs, isLoading, error, certificates, municipalities, linkCertificates = true,
}) => {
  const translate = useTranslate()

  if (isLoading) return <Typography sx={muted}>{translate('ra.page.loading')}</Typography>
  if (error) {
    return <Typography sx={{ fontSize: 13.5, color: 'error.main' }}>{translate('ra.notification.http_error')}</Typography>
  }
  if (!logs || logs.length === 0) {
    return (
      <Typography sx={{ fontSize: 13.5, color: themeColor.slate50 }}>
        {translate('resources.audit_log.empty', { _: 'Geen logregels gevonden.' })}
      </Typography>
    )
  }

  return (
    <Stack component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {logs.map((log, index) => {
        const certificate = certificates?.find(item => item.id === log.certificateId)
        const municipality = municipalities?.find(item => item.id === certificate?.municipalityId)
        const hostname = certificate ? getCommonName(certificate) || certificate.id : null
        return (
          <Box
            component="li"
            key={log.id}
            sx={{
              display: 'flex',
              gap: 1.5,
              position: 'relative',
              pb: index === logs.length - 1 ? 0 : 2,
              // Vertical line connecting the dots.
              ...(index < logs.length - 1 && {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 3.5,
                  top: 14,
                  bottom: 0,
                  borderLeft: `${border.thin} solid ${border.light}`,
                },
              }),
            }}
          >
            <Box
              aria-hidden
              sx={{ width: 8, height: 8, mt: '6px', flexShrink: 0, borderRadius: '50%', bgcolor: themeColor.slate20 }}
            />
            <Box sx={{ minWidth: 0 }}>
              {certificate && (
                <Box sx={{ ...muted, fontSize: 12, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {linkCertificates ? (
                    <Box
                      component={RouterLink}
                      to={`/certificate/${certificate.id}/show`}
                      sx={{
                        'fontFamily': monoFontFamily,
                        'color': themeColor.slate60,
                        'textDecoration': 'none',
                        '&:hover': { color: themeColor.slate, textDecoration: 'underline' },
                      }}
                    >
                      {hostname}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ fontFamily: monoFontFamily, color: themeColor.slate60 }}>{hostname}</Box>
                  )}
                  {municipality && <span>· {municipality.organisatienNaam}</span>}
                </Box>
              )}
              <Typography sx={{ fontSize: 13.5, color: themeColor.slate, overflowWrap: 'anywhere' }}>
                {log.description}
              </Typography>
              <Typography sx={{ ...muted, mt: 0.25 }}>
                {dateFormatter(log.created)}
                {log.user && ` · ${log.user}`}
              </Typography>
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}
